import { Popover as BasePopover } from "@base-ui/react/popover";
import classNames from "classnames";
import * as React from "react";

import styles from "./Popover.module.css";

export interface PopoverProps {
  /**
   * Element that opens and closes the popover. Any component works — a
   * RobotoButton, an icon button, a plain `<button>` — as long as it forwards
   * its ref to the underlying DOM element and spreads unknown props onto it.
   * Base UI merges the popup wiring onto this element: toggle-on-click,
   * `aria-haspopup`/`aria-expanded`/`aria-controls`, and a `data-popup-open`
   * attribute while open (so trigger styles can key off either attribute).
   */
  trigger: React.ReactElement;

  /**
   * Popup contents. Unmounted while closed, so any internal state (search
   * text, tab selection) resets on each open.
   */
  children: React.ReactNode;

  /**
   * Accessible name for the popup, which renders as `role="dialog"`. Provide
   * it whenever the popup has no visible heading.
   */
  "aria-label"?: string;

  /**
   * Side of the trigger the popup attaches to. Flips automatically when
   * there is not enough room.
   * @default "bottom"
   */
  side?: "top" | "bottom" | "left" | "right";

  /**
   * Alignment of the popup relative to the trigger.
   * @default "start"
   */
  align?: "start" | "center" | "end";

  /**
   * Extra class(es) for the popup surface. Size the popup here — width, or a
   * max-height cap via the `--popover-max-height` custom property (the popup
   * never exceeds the viewport regardless).
   */
  className?: string;

  /**
   * Controlled open state. Omit to let the popover manage itself; pair with
   * `onOpenChange` when a consumer needs to close programmatically (e.g. on
   * commit).
   */
  open?: boolean;

  /** Fired when the user opens or closes the popover. */
  onOpenChange?: (open: boolean) => void;

  /**
   * Whether hovering the trigger opens the popup, on top of the click and
   * keyboard wiring that is always present. Base UI bridges the offset
   * between trigger and popup with a safe polygon, so the pointer can travel
   * onto the popup without it closing underfoot — which a hand-rolled
   * `onMouseLeave` on the trigger cannot do.
   *
   * Reserve it for popups that only show information. A popup the user has to
   * act in should stay click-only, so it does not appear under a pointer that
   * was only passing through.
   * @default false
   */
  openOnHover?: boolean;
}

/**
 * A floating panel anchored to a trigger element — the design-system wrapper
 * around Base UI's Popover.
 *
 * Focus moves to the first tabbable element in the popup on open (so a
 * leading search input focuses automatically) and returns to the trigger on
 * close. Escape and outside-click dismiss.
 *
 * @example
 * ```tsx
 * <Popover
 *   trigger={<RobotoButton variant="secondary">Columns</RobotoButton>}
 *   aria-label="Choose columns"
 * >
 *   <PickerBody />
 * </Popover>
 * ```
 */
export function Popover({
  trigger,
  children,
  side = "bottom",
  align = "start",
  className,
  open,
  onOpenChange,
  openOnHover = false,
  "aria-label": ariaLabel,
}: PopoverProps) {
  return (
    <BasePopover.Root open={open} onOpenChange={onOpenChange}>
      <BasePopover.Trigger render={trigger} openOnHover={openOnHover} />
      <BasePopover.Portal>
        {/* 8 = --spacing-xs; JS props can't read CSS variables. */}
        <BasePopover.Positioner
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={8}
          className={styles.positioner}
        >
          <BasePopover.Popup
            className={classNames(styles.popup, className)}
            aria-label={ariaLabel}
          >
            {children}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}
